/*  ========================================================================
    # Sections - Teaser
    ========================================================================  */
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';
import { MotionConfig, motion, useMotionValue, useReducedMotion } from 'motion/react';

const Teaser = ({ entries, guid }) => {
  const navRef = useRef(null);
  const wrapperRef = useRef(null);
  const dragX = useMotionValue(0);
  const reduced = useReducedMotion();
  const entriesRef = useRef([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [pointerPosition, setPointerPosition] = useState('10%');

  const getContentTranslateX = (index, activeSlide) => {
    const translateValue = 100;

    if (index === activeSlide) {
      return 0;
    } else if (index > activeSlide) {
      return translateValue;
    } else {
      return -translateValue;
    }
  };

  const getPointerPosition = index => {
    // Position the pointer in the middle of the navigation
    const elementRect = entriesRef.current[index].getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const substract = (document.body.clientWidth - wrapperRect.width) / 2;
    const elementCenter = elementRect.x + elementRect.width / 2;

    return elementCenter - substract;
  };

  const handleActiveSlideChange = index => {
    setActiveSlide(index);
    setPointerPosition(`${getPointerPosition(index)}px`);
  };

  const handleSwipeLeft = (focusTab = false) => {
    if (activeSlide < entries.length - 1) {
      handleActiveSlideChange(activeSlide + 1);
      if (focusTab) {
        entriesRef.current[activeSlide + 1].focus();
      }
    }
  };

  const handleSwipeRight = (focusTab = false) => {
    if (activeSlide > 0) {
      handleActiveSlideChange(activeSlide - 1);
      if (focusTab) {
        entriesRef.current[activeSlide - 1].focus();
      }
    }
  };

  const getEntryImagePosition = entry => {
    const imagePosition = { '--image-position-x': '50%', '--image-position-y': '50%' }; // Default position
    if (entry.image_position.x) {
      imagePosition['--image-position-x'] = `${entry.image_position.x}%`;
    }

    if (entry.image_position.y) {
      imagePosition['--image-position-y'] = `${entry.image_position.y}%`;
    }

    return imagePosition;
  };

  useEffect(() => {
    setPointerPosition(`${getPointerPosition(0)}px`);
  }, [entriesRef]);

  // Observe wrapper resize to update pointer position
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setPointerPosition(`${getPointerPosition(activeSlide)}px`);
    });

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [activeSlide, wrapperRef]);

  const handleKeyDownOnTabList = e => {
    if (e.key === 'ArrowRight') {
      handleSwipeLeft(true);
    }
    if (e.key === 'ArrowLeft') {
      handleSwipeRight(true);
    }
  };

  return (
    <MotionConfig reducedMotion='user'>
      <div className='s-teaser__outer'>
        <div className='s-teaser__nav' ref={navRef}>
          <div className='container'>
            <div className='s-teaser__nav-outer'>
              <div className='s-teaser__nav-list' role='tablist' onKeyDown={handleKeyDownOnTabList}>
                {entries.map((entry, index) => (
                  <motion.button
                    key={index}
                    className={`s-teaser__nav-item ${activeSlide === index ? 'is-active' : ''}`}
                    onClick={e => handleActiveSlideChange(index, e)}
                    ref={e => (entriesRef.current[index] = e)}
                    aria-label={entry.title}
                    role='tab'
                    aria-selected={activeSlide === index}
                    id={`${guid}_tab_${index}`}
                    aria-controls={`${guid}_tabpanel_${index}`}
                    tabIndex={activeSlide === index ? 0 : -1}
                  >
                    {entry.title}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div
          className='s-teaser__wrapper'
          style={{ '--pointer-position': pointerPosition }}
          ref={wrapperRef}
        >
          <div className='container'>
            <motion.div
              drag={reduced ? false : 'x'}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={{ left: 0.35, right: 0.35 }}
              style={{ x: dragX }}
              onDragEnd={() => {
                const x = dragX.get();
                if (x <= -15) handleSwipeLeft();
                if (x >= 15) handleSwipeRight();
              }}
              className='s-teaser__content'
            >
              {entries.map((entry, index) => (
                <motion.div
                  id={`${guid}_tabpanel_${index}`}
                  aria-labelledby={`${guid}_tab_${index}`}
                  role='tabpanel'
                  hidden={activeSlide !== index}
                  animate={{
                    opacity: activeSlide === index ? 1 : 0,
                    translateX: getContentTranslateX(index, activeSlide),
                    visibility: activeSlide === index ? 'visible' : 'hidden'
                  }}
                  transition={{ type: 'spring', dumping: 20, stiffness: 150 }}
                  className={`wysiwyg-block`}
                  key={index}
                  dangerouslySetInnerHTML={{ __html: entry.content }}
                ></motion.div>
              ))}
            </motion.div>
          </div>
          <div className='s-teaser__images'>
            {entries.map((entry, index) => (
              <motion.div
                initial={{ opacity: 0, visibility: 'hidden' }}
                animate={{
                  opacity: activeSlide === index ? 1 : 0,
                  visibility: activeSlide === index ? 'visible' : 'hidden'
                }}
                exit={{ opacity: 0, visibility: 'hidden' }}
                transition={{ type: 'tween', duration: 0.5 }}
                style={getEntryImagePosition(entry)}
                key={index}
                className='s-teaser__image'
                aria-hidden
              >
                <LazyImage
                  alt={entry.alt}
                  defaultUrl={entry.image['sizes']['large']}
                  placeholderUrl={entry.image['sizes']['placeholder']}
                >
                  <LazySource srcSet={entry.image['sizes']['2048x2048']} mediaMin='1440px' />
                  <LazySource srcSet={entry.image['sizes']['1536x1536']} mediaMin='1280px' />
                  <LazySource srcSet={entry.image['sizes']['1536x1536']} mediaMin='750px' />
                </LazyImage>
              </motion.div>
            ))}
          </div>
        </div>
        <div className='s-teaser__scrollbar'>
          <motion.span
            initial={false}
            animate={{
              left: `${(activeSlide / entries.length) * 100}%`,
              right: `${((entries.length - activeSlide - 1) / entries.length) * 100}%`
            }}
            transition={{ type: 'spring', damping: 20, stiffness: 150 }}
          >
            {' '}
          </motion.span>
        </div>
      </div>
    </MotionConfig>
  );
};

Teaser.propTypes = {
  entries: PropTypes.array,
  guid: PropTypes.string
};

export default Teaser;
